import { parseISO, addMonths, addDays, differenceInDays } from 'date-fns';
import type { ContaPagar, ContaReceber } from '@/services/financeiroService';

export type SortDirection = 'asc' | 'desc';

export type ColunaOrdenacaoContaPagar =
  | 'descricao'
  | 'categoria'
  | 'fornecedor'
  | 'data_vencimento'
  | 'valor'
  | 'tipo_pagamento'
  | 'forma_pagamento'
  | 'status';

export type ColunaOrdenacaoContaReceber =
  | 'descricao'
  | 'categoria'
  | 'cliente'
  | 'data_vencimento'
  | 'valor'
  | 'tipo_pagamento'
  | 'forma_pagamento'
  | 'status';

function compareStrings(a: string, b: string, direction: SortDirection): number {
  const result = a.localeCompare(b, 'pt-BR');
  return direction === 'asc' ? result : -result;
}

function compareNumbers(
  a: number | null | undefined,
  b: number | null | undefined,
  direction: SortDirection
): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  const result = a - b;
  return direction === 'asc' ? result : -result;
}

function compareDates(a: string, b: string, direction: SortDirection): number {
  const aDate = parseDateLocal(a).getTime();
  const bDate = parseDateLocal(b).getTime();
  const result = aDate - bDate;
  return direction === 'asc' ? result : -result;
}

export function ordenarContasPagar(
  contas: ContaPagar[],
  coluna: ColunaOrdenacaoContaPagar,
  direcao: SortDirection
): ContaPagar[] {
  return [...contas].sort((a, b) => {
    switch (coluna) {
      case 'descricao':
        return compareStrings(a.descricao || '', b.descricao || '', direcao);
      case 'categoria':
        return compareStrings(a.categoria || '', b.categoria || '', direcao);
      case 'fornecedor':
        return compareStrings(a.fornecedor || '', b.fornecedor || '', direcao);
      case 'data_vencimento':
        return compareDates(a.data_vencimento, b.data_vencimento, direcao);
      case 'valor':
        return compareNumbers(a.valor, b.valor, direcao);
      case 'tipo_pagamento':
        return compareStrings(a.tipo_pagamento || '', b.tipo_pagamento || '', direcao);
      case 'forma_pagamento':
        return compareStrings(a.forma_pagamento || '', b.forma_pagamento || '', direcao);
      case 'status':
        return compareStrings(a.status || '', b.status || '', direcao);
      default:
        return 0;
    }
  });
}

export function ordenarContasReceber(
  contas: ContaReceber[],
  coluna: ColunaOrdenacaoContaReceber,
  direcao: SortDirection
): ContaReceber[] {
  return [...contas].sort((a, b) => {
    switch (coluna) {
      case 'descricao':
        return compareStrings(a.descricao || '', b.descricao || '', direcao);
      case 'categoria':
        return compareStrings(a.categoria || '', b.categoria || '', direcao);
      case 'cliente':
        return compareStrings(a.cliente || '', b.cliente || '', direcao);
      case 'data_vencimento':
        return compareDates(a.data_vencimento, b.data_vencimento, direcao);
      case 'valor':
        return compareNumbers(a.valor, b.valor, direcao);
      case 'tipo_pagamento':
        return compareStrings(a.tipo_pagamento || '', b.tipo_pagamento || '', direcao);
      case 'forma_pagamento':
        return compareStrings(a.forma_pagamento || '', b.forma_pagamento || '', direcao);
      case 'status':
        return compareStrings(a.status || '', b.status || '', direcao);
      default:
        return 0;
    }
  });
}

// Função helper para obter data atual no timezone local (YYYY-MM-DD)
// Evita problemas de conversão UTC que podem mudar o dia
export const getCurrentDateLocal = (): string => {
  const now = new Date();
  // Usar métodos que trabalham no timezone local
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Função helper para parsear uma data no formato YYYY-MM-DD no timezone local
// Evita problemas de conversão UTC que podem mudar o dia
export const parseDateLocal = (dateString: string): Date => {
  // Se a string já está no formato YYYY-MM-DD, parsear localmente
  const parts = dateString.split('-');
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Mês é 0-indexed no Date
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }
  // Fallback para parseISO se o formato for diferente
  return parseISO(dateString);
};

export type ModalidadeParcelamento = 'mesmo_dia' | '28_dias';

/**
 * Calcula a data de vencimento de uma parcela com base na modalidade de parcelamento.
 * @param dataReferencia Data da parcela de referência (ex: primeira parcela)
 * @param numeroParcela Número da parcela (1-based)
 * @param parcelaReferencia Número da parcela de referência (1-based). Ex: 1 para criação inicial, ou N ao adicionar parcelas após a parcela N
 * @param modalidade 'mesmo_dia' = mesmo dia do mês; '28_dias' = 28 dias entre parcelas
 */
export function calcularDataVencimentoParcela(
  dataReferencia: Date,
  numeroParcela: number,
  parcelaReferencia: number,
  modalidade: ModalidadeParcelamento | null | undefined
): Date {
  const diferenca = numeroParcela - parcelaReferencia;
  if (modalidade === '28_dias') {
    return addDays(dataReferencia, diferenca * 28);
  }
  // mesmo_dia ou null (retrocompatível)
  return addMonths(dataReferencia, diferenca);
}

/**
 * Infere a modalidade de parcelamento quando não está salva no banco (transações criadas antes do campo existir).
 * Analisa as datas das parcelas: se os intervalos forem ~28 dias, retorna '28_dias'; se for mesmo dia do mês, retorna 'mesmo_dia'.
 */
export function inferirModalidadeParcelamento(
  parcelas: { numero_parcela?: number | null; data_vencimento: string }[]
): ModalidadeParcelamento {
  if (!parcelas || parcelas.length < 2) return 'mesmo_dia';
  const ordenadas = [...parcelas].sort((a, b) => (a.numero_parcela ?? 0) - (b.numero_parcela ?? 0));
  let totalDias = 0;
  let contagemIntervalos = 0;
  for (let i = 1; i < ordenadas.length; i++) {
    const prev = parseDateLocal(ordenadas[i - 1].data_vencimento);
    const curr = parseDateLocal(ordenadas[i].data_vencimento);
    const dias = differenceInDays(curr, prev);
    totalDias += dias;
    contagemIntervalos++;
  }
  const mediaDias = contagemIntervalos > 0 ? totalDias / contagemIntervalos : 0;
  // Intervalo ~28 dias (26-30) indica modalidade 28_dias
  if (mediaDias >= 26 && mediaDias <= 30) return '28_dias';
  return 'mesmo_dia';
}

/** Categoria usada em contas_pagar para comissão de carregadores ao parceiro; pagamentos marcados como pagos aparecem na aba Financeiro do parceiro. */
export const CATEGORIA_COMISSAO_CARREGADORES_PARCEIRO = 'Comissões Parceiros (Carregadores)';

/** Categoria usada em contas_pagar para comissão ao parceiro pai (carregadores da rede); exige parceiro_id do parceiro pai. */
export const CATEGORIA_COMISSAO_PARCEIRO_PAI = 'Comissões Parceiros (Pai)';

export const CATEGORIAS = [
  'Fornecedores',
  'Comissões Parceiros',
  'Comissões Parceiros (Pai)',
  'Comissões Parceiros (Carregadores)',
  'Comissões Vendedores',
  'Impostos',
  'Salários',
  'Aluguel',
  'Serviços',
  'Materiais',
  'Despesa Recorrente',
  'Financiamento',
  'Empréstimos',
  'Deslocamento',
  'Hospedagem',
  'Outros',
];


